import Constants from '../../assests/Constants/constants';
import { beautifyNumber } from '../../Utils/FormatNumber';

export const ANNUAL_REPORT_STORAGE_KEYS = {
    caseId: 'annualReport_caseId',
    year: 'annualReport_year',
    activeSection: 'annualReport_activeSection',
};

/** Mirrors Server Enums.AnnualReportProfitAndLossSource. */
export const ProfitAndLossSource = {
    LedgerTrialBalance: 0,
    SortCodeReport: 1,
};

/** Where LedgerReport and ProfitAndLossReport keep their date basis (תאריך מסמך / חודש רישום). */
const REPORT_DATE_BASIS_STORAGE_KEYS = {
    [ProfitAndLossSource.LedgerTrialBalance]: 'ledgerReportFilterByDocumentDate',
    [ProfitAndLossSource.SortCodeReport]: 'profitAndLossFilterByDocumentDate',
};

/** The date basis the report page of a source is showing: document date unless switched there. */
export const readReportDateBasis = (sourceType) => {
    try {
        const stored = localStorage.getItem(REPORT_DATE_BASIS_STORAGE_KEYS[sourceType]);
        return stored === null ? true : stored === 'true';
    } catch {
        return true;
    }
};

/** Mirrors Server Enums.AnnualReportLineKeyType. */
export const LineKeyType = {
    AccountCard: 0,
    SortCode: 1,
    Manual: 2,
    Depreciation: 3,
};

/** A line posted from the asset register (form 1342 / 1343): kept on refetch and deletable like a typed-in row. */
export const isRegisterLine = (line) => line?.lineKeyType === LineKeyType.Depreciation;

/** Mirrors Server Enums.AnnualReportNoteType. */
export const NoteType = {
    Ongoing: 0,
    ReceiptsPayments: 1,
    Audit: 2,
};

/** Mirrors Server Enums.AnnualReportNoteStatus. */
export const NoteStatus = {
    None: 0,
    InReview: 1,
    WaitingForClient: 2,
    NeedsFurtherHandling: 3,
    Done: 4,
};

export const NOTE_STATUS_OPTIONS = [
    NoteStatus.None,
    NoteStatus.InReview,
    NoteStatus.WaitingForClient,
    NoteStatus.NeedsFurtherHandling,
    NoteStatus.Done,
];

/** Mirrors Server Enums.AnnualReportAnnexBSection. */
export const AnnexBSection = {
    HouseProperty: 1,
    KeyMoney: 2,
    Residential10: 3,
    OtherIncome: 4,
    BusinessAssetRental: 5,
    Partners: 6,
    OtherAsset: 7,
};

/** Mirrors Server Enums.AnnualReportAnnexBCategory. */
export const AnnexBCategory = {
    Income: 1,
    Depreciation: 2,
    ExtraDepreciation: 3,
    Regulation1989: 4,
    TaxesAndFees: 5,
    Repairs: 6,
    OtherExpenses: 7,
};

/** Mirrors Server Enums.AnnualReportSpouse. */
export const AnnualReportSpouse = {
    Registered: 1,
    Partner: 2,
    Both: 3,
};

/** Mirrors Server Enums.AnnualReportTrackingStatus. */
export const AnnualReportTrackingStatus = {
    NotHandled: 0,
    InProgress: 1,
    WaitingForClient: 2,
    ReadyToSubmit: 3,
    Submitted: 4,
    Assessed: 5,
};

export const TRACKING_STATUS_META = {
    [AnnualReportTrackingStatus.NotHandled]: { key: 'notHandled', color: '#9aa3b2' },
    [AnnualReportTrackingStatus.InProgress]: { key: 'inProgress', color: '#3366ff' },
    [AnnualReportTrackingStatus.WaitingForClient]: { key: 'waitingForClient', color: '#ff8300' },
    [AnnualReportTrackingStatus.ReadyToSubmit]: { key: 'readyToSubmit', color: '#6b34e8' },
    [AnnualReportTrackingStatus.Submitted]: { key: 'submitted', color: '#1d9a5b' },
    [AnnualReportTrackingStatus.Assessed]: { key: 'assessed', color: '#157a47' },
};

export const ANNEX_B_SECTION_KEYS = {
    [AnnexBSection.HouseProperty]: 'houseProperty',
    [AnnexBSection.KeyMoney]: 'keyMoney',
    [AnnexBSection.Residential10]: 'residential10',
    [AnnexBSection.OtherIncome]: 'otherIncome',
    [AnnexBSection.BusinessAssetRental]: 'businessAssetRental',
    [AnnexBSection.Partners]: 'partners',
    [AnnexBSection.OtherAsset]: 'otherAsset',
};

export const ANNEX_B_CATEGORY_KEYS = {
    [AnnexBCategory.Income]: 'income',
    [AnnexBCategory.Depreciation]: 'depreciation',
    [AnnexBCategory.ExtraDepreciation]: 'extraDepreciation',
    [AnnexBCategory.Regulation1989]: 'regulation1989',
    [AnnexBCategory.TaxesAndFees]: 'taxesAndFees',
    [AnnexBCategory.Repairs]: 'repairs',
    [AnnexBCategory.OtherExpenses]: 'otherExpenses',
};

/**
 * Form 1321 section 3 is "income from other property" up to 2022 and "residential rent at 10%" from 2023;
 * from 2024 section 4 reads "other income / other property".
 */
export const ANNEX_B_RESIDENTIAL_FROM_YEAR = 2023;
export const ANNEX_B_COMBINED_OTHER_FROM_YEAR = 2024;

/** Official form 1321 section order for a tax year; mirrors Server AnnexBFormDefinitions. */
export const annexBSectionsForYear = (year) => [
    AnnexBSection.HouseProperty,
    AnnexBSection.KeyMoney,
    Number(year) >= ANNEX_B_RESIDENTIAL_FROM_YEAR ? AnnexBSection.Residential10 : AnnexBSection.OtherAsset,
    AnnexBSection.OtherIncome,
    AnnexBSection.BusinessAssetRental,
    AnnexBSection.Partners,
];

/** The section a tag lands in on that year's form (sections missing from the year fold into their successor). */
export const annexBEffectiveSection = (section, year) => {
    if (section == null) return null;
    const residential = Number(year) >= ANNEX_B_RESIDENTIAL_FROM_YEAR;
    if (section === AnnexBSection.OtherAsset && residential) return AnnexBSection.OtherIncome;
    if (section === AnnexBSection.Residential10 && !residential) return AnnexBSection.OtherAsset;
    return section;
};

export const annexBSectionNumber = (section, year) => {
    const index = annexBSectionsForYear(year).indexOf(annexBEffectiveSection(section, year));
    return index >= 0 ? index + 1 : null;
};

/** Translation key of a section title as printed on that year's form. */
export const annexBSectionLabelKey = (section, year) => {
    const effective = annexBEffectiveSection(section, year);
    if (effective === AnnexBSection.OtherIncome && Number(year) >= ANNEX_B_COMBINED_OTHER_FROM_YEAR) {
        return 'annualReport.annexB.sections.otherIncomeOrAsset';
    }
    return `annualReport.annexB.sections.${ANNEX_B_SECTION_KEYS[effective]}`;
};

/** Sections a P&L line can be tagged to (partners carries no amounts). */
export const annexBLineSectionsForYear = (year) => annexBSectionsForYear(year)
    .filter((section) => section !== AnnexBSection.Partners);

/** Categories offered per section: house property breaks expenses down like lines 1.1–1.4 of the form. */
export const annexBCategoriesForSection = (section) => (section === AnnexBSection.HouseProperty
    ? [
        AnnexBCategory.Income,
        AnnexBCategory.Depreciation,
        AnnexBCategory.ExtraDepreciation,
        AnnexBCategory.Regulation1989,
        AnnexBCategory.TaxesAndFees,
        AnnexBCategory.Repairs,
        AnnexBCategory.OtherExpenses,
    ]
    : [AnnexBCategory.Income, AnnexBCategory.OtherExpenses]);

export const NOTE_STATUS_META = {
    [NoteStatus.None]: { key: 'None', color: '#9aa3b2' },
    [NoteStatus.InReview]: { key: 'InReview', color: '#3366ff' },
    [NoteStatus.WaitingForClient]: { key: 'WaitingForClient', color: '#ff8300' },
    [NoteStatus.NeedsFurtherHandling]: { key: 'NeedsFurtherHandling', color: '#d92724' },
    [NoteStatus.Done]: { key: 'Done', color: '#1d9a5b' },
};

export const isAuthorizedDealer = (client) =>
    (client?.caseType ?? '').toString().trim() === Constants.AuthorizedDealerCaseType;

export const defaultTaxYear = () => new Date().getFullYear() - 1;

export const readStoredYear = () => {
    const stored = Number(localStorage.getItem(ANNUAL_REPORT_STORAGE_KEYS.year));
    return Number.isInteger(stored) && stored >= 2000 && stored <= new Date().getFullYear() + 1
        ? stored
        : defaultTaxYear();
};

export const noteStatusKey = (lineKeyType, lineKey) => `${lineKeyType}:${lineKey}`;

/** Translates `annualReport.errors.<KEY>` server error keys, falling back to the raw message. */
export const translateServerError = (t, error, fallbackKey = 'annualReport.errors.generic') => {
    const raw = (error?.message || '').trim();
    if (raw) {
        const key = `annualReport.errors.${raw}`;
        const translated = t(key);
        if (translated && translated !== key) return translated;
        if (!raw.startsWith('{') && raw.length < 200) return raw;
    }
    return t(fallbackKey);
};

const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

export const formatAmount = (value) => String(beautifyNumber(toNumber(value))).trim();

export const percentOf = (part, total) => {
    const denominator = toNumber(total);
    if (!denominator) return null;
    return (toNumber(part) / denominator) * 100;
};

export const downloadBlob = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const formatPercent = (value) =>
    value == null ? '' : `${value.toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
